<template>
  <BLayout
    :colors="$site.colors"
    :header-title="$t('pages.customer.register.title')"
    :header-back-route="{ path: '/customer' }"
  >
    <BContainer>
      <div v-if="loading" class="text-center">
        <BSpinner label="Loading" class="m-3 mx-auto" type="grow" />
      </div>
      <CustomerElementsCreate
        v-else-if="response"
        :response="response"
        create
        @success="handleCreateAccountSuccess"
      />
    </BContainer>
  </BLayout>
</template>

<script setup lang="ts">
import * as Fields from "@/components/fields";
import type { ApiResponse } from "@/api/controller";
import type { Customer, NewCustomerData } from "@@/types/api";

const { $api, $site } = useNuxtApp();
const router = useRouter();

definePageMeta({
  tabs: ["user"],
  middleware: [
    'redirects',
    'register'
  ]
})

useSeoMeta({
  robots: 'noindex, nofollow'
})

const loading = ref<boolean>(true);
const response = ref<ApiResponse<any>>();

const newCustomerData = reactive<NewCustomerData>({
  fields: {
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    dateOfBirth: null,
  },
  customFields: [],
  privacyConsentAccepted: false,
  marketingConsentAccepted: false,
});

const handleCreateAccountSuccess = (customer: Customer) => {
  router.push({ path: "/customer/register/success", query: { email: customer.email } });
};

onMounted(async () => {
  loading.value = true;
  const res = await $api.booking.customer();

  if (res) {
    response.value = res;

    if (res.fields && response.value?.fields) {
      for (let i = 0; i < response.value?.fields.length; i++) {
        const field: Record<string, any> = response.value?.fields[i];
  
        if (field.type === "text") {
          field.component = Fields.TextField;
        } else if (field.type === "textarea") {
          field.component = Fields.TextAreaField;
        } else if (field.type === "list") {
          field.component = Fields.ListField;
  
          if (field.custom && field.customField) {
            const options: any = {};
            for (let j = 0; j < field.customField.multipleValues.length; j++) {
              const option = field.customField.multipleValues[j];
              options[option] = option;
            }
  
            field.componentProps = { options };
          } else {
            field.componentProps = { options: field.options };
          }
        } else if (field.type === "checkbox") {
          field.component = Fields.ToggleField;
        } else if (field.type === "date") {
          field.component = Fields.DateField;
          if (field.field === "dateOfBirth") {
            field.componentProps = {
              augmenter: 'ymd',
            };
          } else {
            field.componentProps = {
              augmenter: 'timestamp',
            };
          }
        } else {
          field.component = Fields.TextField;
        }
  
        if (field.custom && field.customField) {
          if (newCustomerData.customFields[field.customField.id] === undefined) {
            newCustomerData.customFields[field.customField.id] =
              field.customField.defaultValue;
          }
        }
      }
    } else {
      navigateTo('/')
    }
  } else {
    navigateTo('/')
  }

  loading.value = false;
});
</script>
